import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import * as serviceActions from '../../actions/service';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn, MikaAction } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import MikaExport from '../../components/MikaExport/MikaExport';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import ServicingCardGrid from './ServicingCardGrid';
import ServicingMapView from './ServicingMapView';
import { SERVICING_STATUS_COLORS, SERVICING_PRIORITY_COLORS, isMobile } from '../../constants/constants';
import { ServicingStats, Pagination } from '../../reducers/service';
import '../Contact/Contact.scss';

var VIEW_STORAGE_KEY = 'mikasoft_servicing_view';

var fmtDate = function(d: string | null): string {
    if (!d) return '-';
    var dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return String(dt.getDate()).padStart(2,'0') + '/' + String(dt.getMonth()+1).padStart(2,'0') + '/' + dt.getFullYear();
};

var fmtDateTime = function(d: string | null): string {
    if (!d) return '-';
    var dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return String(dt.getDate()).padStart(2,'0') + '/' + String(dt.getMonth()+1).padStart(2,'0') + '/' + dt.getFullYear() + ' ' + String(dt.getHours()).padStart(2,'0') + ':' + String(dt.getMinutes()).padStart(2,'0');
};

type StripFilter = 'registered' | 'diagnosing' | 'waiting' | 'repairing' | 'completed' | 'cities' | 'map';

interface Props {
    stats: ServicingStats | null;
    servicings: any[];
    servicingsPagination: Pagination;
    servicingsCities: any[];
    locations: any[];
    loading: boolean;
    companyProfile: any;
    actions: any;
    history: any;
    location: any;
}

var ServicingListPage: React.FC<Props> = function(props) {
    var intl = useIntl();
    var fm = useCallback(function(id: string, defaultMessage: string, values?: Record<string, any>) {
        return intl.formatMessage({ id: id, defaultMessage: defaultMessage }, values);
    }, [intl]);

    // Backend-provided activeDuration strings are Albanian ("3 dite, 22 ore, 25 min").
    // Parse numbers and reformat via locale keys.
    var formatActiveDurationString = useCallback(function(val: string | null | undefined): string {
        if (!val) return '';
        var str = String(val);
        var dayMatch = str.match(/(\d+)\s*(dit|day|Tag|giorn|día|дена)/i);
        var hourMatch = str.match(/(\d+)\s*(or|hour|Std|ore|hora|час)/i);
        var minMatch = str.match(/(\d+)\s*(min|мин)/i);
        var days = dayMatch ? parseInt(dayMatch[1]) : 0;
        var hours = hourMatch ? parseInt(hourMatch[1]) : 0;
        var minutes = minMatch ? parseInt(minMatch[1]) : 0;
        if (days === 0 && hours === 0 && minutes === 0) return str;
        var parts: string[] = [];
        if (days > 0) parts.push(fm('installation.detail.duration.days', '{count} ditë', { count: days }));
        if (hours > 0) parts.push(fm('installation.detail.duration.hours', '{count} orë', { count: hours }));
        if (minutes > 0 || parts.length === 0) parts.push(fm('installation.detail.duration.minutes', '{count} min', { count: minutes }));
        return parts.join(', ');
    }, [fm]);

    var _urlFilters = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        strip: { default: 'registered', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        city: { default: '', type: 'string' },
    });
    var filters = _urlFilters.filters;
    var setFilter = _urlFilters.setFilter;
    var setFilters = _urlFilters.setFilters;
    var replaceFilters = _urlFilters.replaceFilters;

    var currentPage = filters.page as number;
    var perPage = filters.perPage as number;
    var searchTerm = filters.search as string;
    var stripFilter = (filters.strip || 'registered') as StripFilter;
    var dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    var activeCity = filters.city || null;

    var _vm = useState<'table' | 'card'>(function() {
        var saved = localStorage.getItem(VIEW_STORAGE_KEY);
        return saved === 'card' ? 'card' : 'table';
    }); var viewMode = _vm[0]; var setViewMode = _vm[1];
    var _cpo = useState(false); var cityPopoverOpen = _cpo[0]; var setCityPopoverOpen = _cpo[1];
    var _cpp = useState({ top: 0, left: 0 }); var cityPopoverPos = _cpp[0]; var setCityPopoverPos = _cpp[1];
    var stripRef = useRef<HTMLDivElement>(null);

    useEffect(function() { props.actions.fetchServicingStats(); }, []);

    useEffect(function() { fetchData(); }, [props.location.search]);

    var fetchData = function() {
        if (stripFilter === 'map') return;

        var statusMap: Record<string, any> = {
            registered: 1, diagnosing: 2, waiting: 'waiting', repairing: 4, completed: 6,
        };

        var f: any = {
            page: currentPage,
            per_page: perPage,
            status: statusMap[stripFilter],
        };
        if (searchTerm) f.search = searchTerm;
        if (activeCity) f.city = activeCity;
        if (dateRange?.from) f.start_date = dateRange.from;
        if (dateRange?.to) f.end_date = dateRange.to;

        props.actions.fetchServicings(f);
    };

    useEffect(function() {
        if (stripFilter === 'map') {
            props.actions.fetchServicingLocations({});
        }
    }, [stripFilter]);

    var effectiveView: 'table' | 'card' = isMobile ? 'table' : viewMode;
    var cities = props.servicingsCities || [];

    var dashboardStats = useMemo(function() {
        var s = props.stats;
        if (!s) return [];
        return [
            { key: 'registered', label: fm('service.strip.registered', 'E regjistruar'), value: s.registered ?? 0, icon: 'alert' },
            { key: 'diagnosing', label: fm('service.strip.diagnosing', 'Në diagnostikim'), value: s.diagnosing ?? 0, icon: 'box' },
            { key: 'waiting', label: fm('service.strip.waiting', 'Në pritje'), value: s.waiting ?? 0, icon: 'alert' },
            { key: 'repairing', label: fm('service.strip.repairing', 'Në riparim'), value: s.repairing ?? 0, icon: 'layers' },
            { key: 'completed', label: fm('service.strip.completed', 'E përfunduar'), value: s.completed ?? 0, icon: 'check' },
            { key: 'cities', label: fm('service.strip.cities', 'Qytetet'), value: cities.length, icon: 'map' },
            { key: 'map', label: fm('service.strip.map', 'Harta'), value: '', icon: 'map' },
        ];
    }, [props.stats, cities, fm]);

    var positionPopoverUnderCard = function(idx: number) {
        if (!stripRef.current) return { top: 0, left: 0 };
        var cards = stripRef.current.querySelectorAll('.cds-card');
        if (cards[idx]) { var r = cards[idx].getBoundingClientRect(); return { top: r.bottom + 4, left: r.left }; }
        return { top: 0, left: 0 };
    };

    var handleStripClick = function(key: string) {
        if (key === 'cities') {
            setCityPopoverPos(positionPopoverUnderCard(5));
            setCityPopoverOpen(!cityPopoverOpen);
            return;
        }
        setCityPopoverOpen(false);
        if (key !== stripFilter) {
            setFilters({ strip: key, city: '', page: 1 });
        } else {
            setFilters({ strip: key, page: 1 });
        }
    };

    var handleRowClick = function(row: any) {
        props.history.push('/dashboard/service/' + row.id);
    };

    var handleSearch = function(e: React.ChangeEvent<HTMLInputElement>) { replaceFilters({ search: e.target.value, page: 1 }); };
    var clearSearch = function() { replaceFilters({ search: '', page: 1 }); };
    var handleDateChange = function(range: DateRange | null) { setFilters({ dateFrom: range?.from || '', dateTo: range?.to || '', page: 1 }); };

    // Backend statusLabel is always Albanian; map numeric status → locale.
    // Based on statusMap: 1=registered, 2=diagnosing, 'waiting'=waiting, 4=repairing, 6=completed
    var getServicingStatusLabel = function(row: any) {
        var s = row?.status;
        if (s === 1) return fm('service.strip.registered', 'E regjistruar');
        if (s === 2) return fm('service.strip.diagnosing', 'Në diagnostikim');
        if (s === 3 || s === 'waiting') return fm('service.strip.waiting', 'Në pritje');
        if (s === 4) return fm('service.strip.repairing', 'Në riparim');
        if (s === 6) return fm('service.strip.completed', 'E përfunduar');
        return row?.statusLabel || '';
    };

    // Translate seeded servicing type names. Admin-added types fall back to raw name.
    // See servicing_types migration: Kontroll / Servisim i thjeshte / Servisim i komplikuar.
    var translateTypeName = function(name: string | null | undefined): string {
        if (!name) return '';
        var n = name.toLowerCase().trim();
        if (n === 'kontroll') return fm('service.type.kontroll', 'Kontroll');
        if (n.indexOf('thjesht') !== -1) return fm('service.type.servisimSimple', 'Servisim i thjeshtë');
        if (n.indexOf('komplikuar') !== -1) return fm('service.type.servisimComplex', 'Servisim i komplikuar');
        return name;
    };

    // Priority 1=low, 2=normal, 3=high, 4=urgent (matches SERVICING_PRIORITY_COLORS).
    var getServicingPriorityLabel = function(row: any) {
        var p = row?.priority;
        if (p === 1) return fm('service.priority.low', 'E ulët');
        if (p === 2) return fm('service.priority.normal', 'Normale');
        if (p === 3) return fm('service.priority.high', 'E lartë');
        if (p === 4) return fm('service.priority.urgent', 'Urgjente');
        return row?.priorityLabel || '';
    };

    // Columns
    var columns: MikaColumn[] = useMemo(function() { return [
        { key: 'clientName', title: fm('production.detail.client', 'Klienti'), render: function(_v: any, row: any) { return row.client?.name || '-'; } },
        { key: 'projectName', title: fm('production.detail.project', 'Projekti'), render: function(_v: any, row: any) { return row.project?.name || '-'; } },
        { key: 'status', title: fm('service.col.status', 'Statusi'), width: '130px', render: function(val: number, row: any) {
            var colors = SERVICING_STATUS_COLORS[val] || { color: '#94a3b8', bg: '#f1f5f9' };
            return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: colors.color, background: colors.bg }}>{getServicingStatusLabel(row)}</span>;
        }},
        { key: 'priority', title: fm('service.col.priority', 'Prioriteti'), width: '100px', render: function(val: number, row: any) {
            var colors = SERVICING_PRIORITY_COLORS[val] || { color: '#3b82f6', bg: '#dbeafe' };
            return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, color: colors.color, background: colors.bg }}>{getServicingPriorityLabel(row)}</span>;
        }},
        { key: 'isWarranty', title: fm('service.col.warranty', 'Garancia'), width: '90px', hideOnMobile: true, render: function(val: boolean, row: any) {
            if (!val) return <span style={{ color: '#d0dbe8' }}>—</span>;
            return <span style={{ padding: '3px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, color: row.isWarrantyActive ? '#16a34a' : '#94a3b8', background: row.isWarrantyActive ? '#f0fdf4' : '#f1f5f9' }}>
                {row.isWarrantyActive ? fm('service.warranty.active', 'Garanci') : fm('service.warranty.expired', 'Skaduar')}
            </span>;
        }},
        { key: 'typeName', title: fm('service.col.type', 'Lloji'), hideOnMobile: true, render: function(_v: any, row: any) { return translateTypeName(row.servicingType?.name) || '-'; } },
        { key: 'technicianName', title: fm('service.col.technician', 'Tekniku'), hideOnMobile: true, render: function(_v: any, row: any) { return row.technician?.name || '-'; } },
        { key: 'diagnosis', title: fm('service.col.diagnosis', 'Diagnostika'), hideOnMobile: true, render: function(val: string) {
            if (!val) return <span style={{ color: '#d0dbe8' }}>—</span>;
            return <span title={val}>{val}</span>;
        }},
        { key: 'activeDuration', title: fm('service.col.activeDuration', 'Koha aktive'), hideOnMobile: true, width: '100px', render: function(val: string) { return formatActiveDurationString(val); } },
        { key: 'createdAt', title: fm('service.col.date', 'Data'), sortable: true, hideOnMobile: true, width: '100px', render: function(val: string) { return fmtDate(val); } },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]; }, [intl]);

    var tableActions: MikaAction[] = [
        { icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>, tooltip: fm('service.action.view', 'Shiko'), onClick: handleRowClick },
    ];

    var exportColumns = [
        { key: 'client', title: fm('production.detail.client', 'Klienti'), render: function(row: any) { return row.client?.name || ''; } },
        { key: 'project', title: fm('production.detail.project', 'Projekti'), render: function(row: any) { return row.project?.name || ''; } },
        { key: 'statusLabel', title: fm('service.col.status', 'Statusi'), render: function(row: any) { return getServicingStatusLabel(row); } },
        { key: 'priorityLabel', title: fm('service.col.priority', 'Prioriteti'), render: function(row: any) { return getServicingPriorityLabel(row); } },
        { key: 'warranty', title: fm('service.col.warranty', 'Garancia'), render: function(row: any) {
            if (!row.isWarranty) return fm('service.warranty.exportNo', 'Jo');
            return row.isWarrantyActive ? fm('service.warranty.exportActive', 'Nën garanci') : fm('service.warranty.exportExpired', 'E skaduar');
        }},
        { key: 'type', title: fm('service.col.type', 'Lloji'), render: function(row: any) { return translateTypeName(row.servicingType?.name); } },
        { key: 'technician', title: fm('service.col.technician', 'Tekniku'), render: function(row: any) { return row.technician?.name || ''; } },
        { key: 'diagnosis', title: fm('service.col.diagnosis', 'Diagnostika') },
        { key: 'activeDuration', title: fm('service.col.activeDuration', 'Koha aktive'), render: function(row: any) { return formatActiveDurationString(row.activeDuration); } },
        { key: 'createdAt', title: fm('service.col.date', 'Data'), render: function(row: any) { return fmtDate(row.createdAt); } },
    ];

    var fetchAllForExport = function() {
        var f: any = { per_page: 0 };
        var statusMap: Record<string, any> = { registered: 1, diagnosing: 2, waiting: 'waiting', repairing: 4, completed: 6 };
        if (stripFilter !== 'map' && stripFilter !== 'cities') f.status = statusMap[stripFilter];
        if (searchTerm) f.search = searchTerm;
        if (activeCity) f.city = activeCity;
        if (dateRange?.from) f.start_date = dateRange.from;
        if (dateRange?.to) f.end_date = dateRange.to;
        return props.actions.fetchServicings(f).then(function(res: any) {
            return res?.data || props.servicings;
        });
    };

    return (
        <div className="clp-page">
            <MikaPageHeader
                title={fm('service.title', 'Servisimi')}
                subtitle={fm('service.subtitle.count', '{count} servisime', { count: props.servicingsPagination.total })}
                actions={
                    <button
                        onClick={function() { props.history.push('/dashboard/service/create'); }}
                        style={{
                            padding: '7px 16px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)',
                            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 500,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s',
                        }}
                        onMouseEnter={function(e: any) { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
                        onMouseLeave={function(e: any) { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        <FormattedMessage id="service.createButton" defaultMessage="Krijo Servisim" />
                    </button>
                }
            />

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input className="clp-search-input" type="text" placeholder={fm('service.search.placeholder', 'Kërko servisime...')} value={searchTerm} onChange={handleSearch} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={clearSearch}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={handleDateChange} />
                    {!isMobile && stripFilter !== 'map' && (
                        <MikaViewToggle mode={viewMode} onChange={function(m) { setViewMode(m); localStorage.setItem(VIEW_STORAGE_KEY, m); }} />
                    )}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('service.exportFilename', 'servisime')} title={fm('service.exportTitle', 'Servisime')} companyProfile={props.companyProfile} />
                </div>
            </div>

            <div ref={stripRef}>
                <ContactDashboardStrip stats={dashboardStats} activeFilter={stripFilter} onFilterClick={handleStripClick} />
            </div>

            {/* City popover */}
            {cityPopoverOpen && (
                <div className="clp-city-popover" style={{ top: cityPopoverPos.top, left: cityPopoverPos.left }}>
                    <div className="clp-city-header"><FormattedMessage id="service.cityPopover.header" defaultMessage="Qytetet" /></div>
                    <button className="clp-city-row" onClick={function() { setFilters({ city: '', page: 1 }); setCityPopoverOpen(false); }}>
                        <span className="clp-city-name"><FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" /></span>
                    </button>
                    {cities.map(function(c: any) {
                        return (
                            <button key={c.city} className={'clp-city-row ' + (activeCity === c.city ? 'clp-city-row-active' : '')}
                                onClick={function() { setFilters({ city: c.city, page: 1 }); setCityPopoverOpen(false); }}>
                                <span className="clp-city-name">{c.city}</span>
                                <span className="clp-city-count">{c.count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Active city chip */}
            {activeCity && (
                <div className="clp-active-filter-bar">
                    <div className="clp-active-filter-chip">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        <span>{activeCity}</span>
                        <button className="clp-active-filter-clear" onClick={function() { setFilters({ city: '', page: 1 }); }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Table view */}
            {stripFilter !== 'map' && effectiveView === 'table' && (
                <MikaTable columns={columns} data={props.servicings} actions={tableActions} loading={props.loading}
                    onRowClick={handleRowClick}
                    emptyMessage={fm('service.empty.servicings', 'Nuk ka servisime')} />
            )}

            {/* Card view */}
            {stripFilter !== 'map' && effectiveView === 'card' && (
                <ServicingCardGrid data={props.servicings} onEdit={handleRowClick} />
            )}

            {/* Map view */}
            {stripFilter === 'map' && (
                <ServicingMapView
                    locations={props.locations}
                    onMarkerClick={function(item: any) {
                        if (item.servicingId) props.history.push('/dashboard/service/' + item.servicingId);
                    }}
                />
            )}

            {/* Pagination */}
            {stripFilter !== 'map' && (
                <PaginationComponent currentPage={currentPage} perPage={perPage} pagination={props.servicingsPagination}
                    onPageChange={function(p: number) { setFilter('page', p); }} onPerPageChange={function(pp: number) { setFilters({ perPage: pp, page: 1 }); }} />
            )}
        </div>
    );
};

var mapStateToProps = function(state: any) {
    return {
        stats: state.service.stats,
        servicings: state.service.servicings,
        servicingsPagination: state.service.servicingsPagination,
        servicingsCities: state.service.servicingsCities,
        locations: state.service.locations,
        loading: state.app.loading,
        companyProfile: state.app.companyProfile,
    };
};

var mapDispatchToProps = function(dispatch: any) {
    return {
        actions: bindActionCreators(serviceActions as any, dispatch),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ServicingListPage) as any) as any;
